package com.yogaaicoach.pages.trainingprogram

import com.yogaaicoach.common.ui.components.*
import com.yogaaicoach.dashboardViewModel
import com.yogaaicoach.user.AppViewModel
import react.FC
import react.Props

internal const val AddTrainingExistingHash = "#add-training-existing"
internal const val AddTraining1Hash = "#add-training-1"
internal const val AddTraining2Hash = "#add-training-2"
internal const val AddTraining3Hash = "#add-training-3"
internal const val AddTraining4Hash = "#add-training-4"
internal const val AddTrainingSuccessHash = "#add-training-success"

internal external interface AddNewTrainingFlowPageProps : Props {
    var viewModel: AddNewTrainingViewModel
    var appViewModel: AppViewModel
    var route: String
    var navigateTo: (String) -> Unit
    var navigateReplacing: (String) -> Unit
    var onNavigateToPayment: () -> Unit
    var onNavigateToContactUs: () -> Unit
}

// Wraps the multi-step "create training program" wizard (AddNewTrainingScreen1-4 +
// AddNewTrainingSuccessScreen in the real app) the same way DashboardPage wraps its
// dashboard-mode pages — one shared TopBar, swapping out the body per step. Owns its own
// sub-routes (each wizard step is its own hash) so the browser's native back button steps
// backward through the wizard too, same as the real app's NavController backstack and
// consistent with how every other multi-page flow in this port (the auth funnel) is wired.
internal val AddNewTrainingFlowPage = FC<AddNewTrainingFlowPageProps> { props ->
    TopBar {
        title = "Create Your Program"
        onSignOut = { props.appViewModel.signOut() }
        onNavigateToPayment = props.onNavigateToPayment
        onNavigateToContactUs = props.onNavigateToContactUs
    }

    when (props.route) {
        AddTrainingExistingHash -> AddNewExistingTrainingPage {
            viewModel = props.viewModel
            onCreateBasedOnPrevious = {
                props.viewModel.reset()
                dashboardViewModel.loadPage(props.appViewModel::askNewToken, isReload = true)
                props.navigateReplacing(AddTrainingSuccessHash)
            }
            onCreateNew = { props.navigateTo(AddTraining1Hash) }
        }
        AddTraining2Hash -> AddNewTrainingScreen2Page {
            viewModel = props.viewModel
            onNextButtonClicked = { props.navigateTo(AddTraining3Hash) }
        }
        AddTraining3Hash -> AddNewTrainingScreen3Page {
            viewModel = props.viewModel
            onNextButtonClicked = { props.navigateTo(AddTraining4Hash) }
        }
        AddTraining4Hash -> AddNewTrainingScreen4Page {
            viewModel = props.viewModel
            // Replaces (not pushes) the history entry: the real composeApp's NavController has
            // no popUpTo on this transition either, so pressing back from Success there lands
            // back on the filled-in Screen4 with "Create" still clickable — a real,
            // pre-existing duplicate-submission risk in the source app. Fixed here (jsMain-only
            // deviation) by making back skip past the just-submitted form instead.
            onNextButtonClicked = { props.navigateReplacing(AddTrainingSuccessHash) }
        }
        AddTrainingSuccessHash -> AddNewTrainingSuccessPage {
            onGoToDashboardButtonClicked = {
                // Reset the wizard's state now that the program has been created — otherwise
                // this long-lived singleton ViewModel would still hold this run's goal/level/
                // age-group/weekday selections the next time the user starts a new program.
                props.viewModel.reset()
                // Mirrors Navigation.kt's Dashboard?isReset=true: force DashboardViewModel to
                // refetch (it would otherwise still show stale "no active program" state from
                // before this training program was created).
                dashboardViewModel.loadPage(props.appViewModel::askNewToken, isReload = true)
                props.navigateTo("")
            }
        }
        else -> AddNewTrainingScreen1Page {
            viewModel = props.viewModel
            onNextButtonClicked = { props.navigateTo(AddTraining2Hash) }
        }
    }
}
